<script setup lang="ts">
import { ref, computed } from 'vue'
import { useRouter } from 'vue-router'
import { Warning, OfficeBuilding, Search } from '@element-plus/icons-vue'

const props = withDefaults(defineProps<{
  trigger: 'project_limit' | 'ai_quota' | 'member_invite' | 'active_conversion'
  title?: string
  message?: string
  actionText?: string
  secondaryActionText?: string
  showDismiss?: boolean
}>(), {
  title: '',
  message: '',
  actionText: '立即创建单位',
  secondaryActionText: '查找并加入已有单位',
  showDismiss: true,
})

const emit = defineEmits<{
  (e: 'action'): void
  (e: 'secondary-action'): void
  (e: 'dismiss'): void
}>()

const router = useRouter()
// Dismiss check now handled by parent (App.vue) to avoid mount-then-hide flicker
const visible = ref(true)

// Auto title/message based on trigger
const displayTitle = computed(() => {
  if (props.title) return props.title
  switch (props.trigger) {
    case 'project_limit': return '项目数量已达上限'
    case 'ai_quota': return 'AI 额度已用完'
    case 'member_invite': return '团队协作需要正式单位'
    default: return '升级解锁更多功能'
  }
})

const displayMessage = computed(() => {
  if (props.message) return props.message
  switch (props.trigger) {
    case 'project_limit': return '免费版仅支持创建 1 个台账项目。创建或加入正式单位即可解锁更多。'
    case 'ai_quota': return '本月免费 AI 额度已用完。创建或加入正式单位即可继续使用。'
    case 'member_invite': return '个人体验版不支持邀请成员。创建正式单位即可开启团队协作。'
    default: return '创建或加入正式单位，解锁完整功能体验。'
  }
})

function handleAction() {
  visible.value = false
  emit('action')
  router.push('/setup')
}

function handleSecondaryAction() {
  visible.value = false
  emit('secondary-action')
  router.push('/settings/organization')
}

function handleDismiss() {
  visible.value = false
  // Remember dismissal for 24h per trigger type (stored as date string)
  const key = `create_org_prompt_dismissed_${props.trigger}`
  const until = new Date(Date.now() + 24 * 60 * 60 * 1000).toISOString().slice(0, 10)
  localStorage.setItem(key, until)
  emit('dismiss')
}
</script>

<template>
  <el-dialog
    v-model="visible"
    :show-close="false"
    width="440px"
    align-center
    class="create-org-prompt"
  >
    <div class="text-center">
      <div class="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-amber-100">
        <ElIcon size="32" class="text-amber-500"><Warning /></ElIcon>
      </div>
      <h3 class="mb-2 text-lg font-semibold text-[var(--color-title)]">{{ displayTitle }}</h3>
      <p class="mb-6 text-sm text-[var(--color-body)]">{{ displayMessage }}</p>
      <div class="space-y-3">
        <el-button type="primary" class="w-full" @click="handleAction">
          <ElIcon class="mr-1"><OfficeBuilding /></ElIcon>
          {{ actionText }}
        </el-button>
        <el-button class="w-full" @click="handleSecondaryAction">
          <ElIcon class="mr-1"><Search /></ElIcon>
          {{ secondaryActionText }}
        </el-button>
        <el-button v-if="showDismiss" text class="w-full text-[var(--color-caption)]" @click="handleDismiss">
          暂时不用
        </el-button>
      </div>
    </div>
  </el-dialog>
</template>

<style scoped>
.create-org-prompt :deep(.el-dialog__header) {
  display: none;
}
.create-org-prompt :deep(.el-dialog__body) {
  padding: 32px 24px;
}
</style>
